import React, { useState } from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { type PromiseState, initialPromiseState, promiseStateRender } from '../util/PromiseState'
import { createPromiseManager, cancelPromiseManagers } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import { Pulse } from '../ui/Pulse'
import { AnnotationCanvas, Shape } from './AnnotationCanvas'
import i18next from 'i18next'
import type { Nullable } from '@zaptic-external/zorm/dist/helpers/types'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import classNames from 'classnames'

enum AnnotationMode {
    Draw,
}

const COLORS = ['#FFFFFF', '#000000', '#6DD400', '#E44967', '#59B2F2', '#F58722', '#FFE609']

interface Props {
    deps: CommonDeps
    photoState: PromiseState<string | null>
    annotationState: PromiseState<string | null>
    onCancel: () => void
    onSave: (blob: Blob) => Promise<void>
}

interface State {
    saveState: PromiseState<void>
    mode: Nullable<AnnotationMode>
    color: string
    isColorSelectOpen: boolean
    editHistory: string[]
    isDrawing: boolean
    isObjectSelected: boolean
    isTextInputFocused: boolean
}

export class AnnotationPage extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        saveState: initialPromiseState,
        mode: null,
        color: COLORS[0],
        isColorSelectOpen: false,
        editHistory: [],
        isDrawing: false,
        isObjectSelected: false,
        isTextInputFocused: false,
    }

    private annotationCanvas: AnnotationCanvas

    private promiseManagers = {
        save: createPromiseManager(() => this.props.deps, createStateLens(this, 'saveState')),
    }

    public componentDidMount() {
        document.documentElement.classList.add('hide-bottom-elements')
    }

    public componentWillUnmount() {
        document.documentElement.classList.remove('hide-bottom-elements')

        cancelPromiseManagers(this.promiseManagers)
    }

    public componentDidUpdate(_: Props, prevState: State) {
        if (prevState.mode !== this.state.mode) {
            this.handleModeChange()
        }

        if (prevState.color !== this.state.color) {
            this.annotationCanvas.setColor(this.state.color)
        }
    }

    private handleCanvasUpdate = (snapshot: string) => {
        this.setState({
            editHistory: [...this.state.editHistory, snapshot],
        })
    }

    private handleModeChange() {
        switch (this.state.mode) {
            case AnnotationMode.Draw:
                this.annotationCanvas.enableDrawingMode()
                break
            case null:
                this.annotationCanvas.disableEditMode()
                break
            default:
                assumeExhaustion(this.state.mode)
        }
    }

    private onClear = () => {
        if (this.annotationCanvas == null) return
        this.annotationCanvas.clear()
    }

    private onSave = () => {
        if (this.annotationCanvas == null) return
        const blob = this.annotationCanvas.toBlob()
        this.promiseManagers.save.trigger(() => this.props.onSave(blob))
    }

    private enableFreedraw = () => {
        this.setState({
            editHistory: [this.annotationCanvas.snapshot],
            mode: AnnotationMode.Draw,
        })
    }

    private onEditFinish = () => {
        this.annotationCanvas.discardSelection()
        this.setState({
            editHistory: [],
            mode: null,
        })
    }

    private enableTextMode = () => {
        this.annotationCanvas.addTextElement()
        this.setState({
            editHistory: [this.annotationCanvas.snapshot],
        })
    }

    private handleShapeSelect = (shape: Shape) => {
        this.annotationCanvas.addShape(shape)
        this.setState({
            editHistory: [],
            mode: null,
        })
    }

    private onEditClear = () => {
        const [initialState] = this.state.editHistory
        this.setState({
            mode: null,
            editHistory: [],
        })

        if (this.annotationCanvas == null || initialState == null) return
        this.annotationCanvas.loadFromJSON(initialState)
    }

    private handleUndoClick = () => {
        if (this.annotationCanvas == null) return
        const targetState = this.state.editHistory[this.state.editHistory.length - 2]
        if (targetState == null) return
        this.annotationCanvas.loadFromJSON(targetState)
        this.setState({
            editHistory: this.state.editHistory.slice(0, -1),
        })
    }

    private handleColorChange = (color: string) => {
        this.setState({
            color,
        })
    }

    private handleDrawEnd = () => {
        this.setState({
            isDrawing: false,
        })
    }

    private handleDrawStart = () => {
        this.setState({
            isDrawing: true,
        })
    }

    private handleSelectionChange = (object: fabric.Object) => {
        this.setState({
            isObjectSelected: object != null,
        })
    }

    private handleDeleteClick = () => {
        this.annotationCanvas.deleteActiveObject()
    }

    private handleTextEditStart = () => {
        this.setState({
            isTextInputFocused: true,
        })
    }

    private handleTextEditEnd = () => {
        this.setState({
            isTextInputFocused: false,
        })
    }

    private handleTextEditDoneClick = () => {
        this.annotationCanvas.endTextEdit()
    }

    public tryRender = () => {
        return (
            <div className="AppCore__photo__AnnotationPage">
                {this.renderTopBar()}
                <div className="AppCore__photo__AnnotationPage__content">
                    {promiseStateRender(this.props.deps, this.props.photoState, (photoUrl) =>
                        promiseStateRender(this.props.deps, this.props.annotationState, (annotation) => (
                            <AnnotationCanvas
                                ref={(ref) => (this.annotationCanvas = ref)}
                                onChange={this.handleCanvasUpdate}
                                onDrawStart={this.handleDrawStart}
                                onDrawEnd={this.handleDrawEnd}
                                deps={this.props.deps}
                                photoUrl={photoUrl}
                                annotationUrl={annotation}
                                onSelectionChange={this.handleSelectionChange}
                                onTextEditEnd={this.handleTextEditEnd}
                                onTextEditStart={this.handleTextEditStart}
                            />
                        ))
                    )}
                </div>
                {this.renderBottomBar()}
            </div>
        )
    }

    private renderTopBar() {
        if (this.state.isDrawing) return null

        const cancelLabel = i18next.t('appcore:common.cancel')

        if (this.state.isTextInputFocused) {
            return (
                <div className="AppCore__photo__AnnotationPage__topBar">
                    <div className="AppCore__photo__AnnotationPage__topBarContent">
                        <div className="AppCore__photo__AnnotationPage__cancel" onClick={this.onEditClear}>
                            {cancelLabel}
                        </div>
                        <div className="AppCore__photo__AnnotationPage__done" onClick={this.handleTextEditDoneClick}>
                            Done
                        </div>
                    </div>
                </div>
            )
        }

        if (this.state.mode === AnnotationMode.Draw || this.state.isObjectSelected) {
            return (
                <div className="AppCore__photo__AnnotationPage__topBar">
                    <div className="AppCore__photo__AnnotationPage__topBarContent">
                        <div className="AppCore__photo__AnnotationPage__cancel" onClick={this.onEditClear}>
                            {cancelLabel}
                        </div>
                        <div className="AppCore__photo__AnnotationPage__actionSelect">
                            <div className="AppCore__photo__AnnotationPage__action" onClick={this.onEditFinish}>
                                <zaptic-icon icon-name="tick" />
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        const clearLabel = i18next.t('appcore:AnnotationPage.clearAll')
        const drawLabel = i18next.t('appcore:AnnotationPage.draw')
        const textLabel = i18next.t('appcore:AnnotationPage.text')

        return (
            <div className="AppCore__photo__AnnotationPage__topBar">
                <div className="AppCore__photo__AnnotationPage__topBarContent">
                    <div className="AppCore__photo__AnnotationPage__clear" onClick={this.onClear}>
                        <div className="AppCore__photo__AnnotationPage__clearIcon">
                            <zaptic-icon icon-name="retry"></zaptic-icon>
                        </div>
                        <div className="AppCore__photo__AnnotationPage__clearLabel">{clearLabel}</div>
                    </div>
                    <div className="AppCore__photo__AnnotationPage__actionSelect">
                        <div className="AppCore__photo__AnnotationPage__action" onClick={this.enableTextMode}>
                            <div className="AppCore__photo__AnnotationPage__actionIcon">
                                <zaptic-icon class="test" icon-name="text" />
                            </div>
                            <div className="AppCore__photo__AnnotationPage__actionLabel">{textLabel}</div>
                        </div>

                        <div className="AppCore__photo__AnnotationPage__action" onClick={this.enableFreedraw}>
                            <div className="AppCore__photo__AnnotationPage__actionIcon">
                                <zaptic-icon class="test" icon-name="annotate" />
                            </div>
                            <div className="AppCore__photo__AnnotationPage__actionLabel">{drawLabel}</div>
                        </div>

                        <ShapeSelector onShapeSelect={this.handleShapeSelect} />
                    </div>
                </div>
            </div>
        )
    }

    private renderBottomBarActionButton() {
        if (this.state.isColorSelectOpen) return <div />
        if (this.state.isObjectSelected) {
            const deleteLabel = i18next.t('appcore:common.delete')
            return (
                <div className="AppCore__photo__AnnotationPage__bottomBarAction" onClick={this.handleDeleteClick}>
                    <zaptic-icon class="AppCore__photo__AnnotationPage__bottomBarActionIcon" icon-name="bin" />
                    <div className="AppCore__photo__AnnotationPage__bottomBarActionLabel">{deleteLabel}</div>
                </div>
            )
        }

        const undoLabel = i18next.t('appcore:common.undo')

        return (
            <div
                className={classNames('AppCore__photo__AnnotationPage__bottomBarAction', {
                    'AppCore__photo__AnnotationPage__bottomBarAction--disabled':
                        this.state.editHistory.length < 2 || this.state.isDrawing,
                })}
                onClick={this.handleUndoClick}
            >
                <zaptic-icon class="AppCore__photo__AnnotationPage__bottomBarActionIcon" icon-name="retry" />
                <div className="AppCore__photo__AnnotationPage__bottomBarActionLabel">{undoLabel}</div>
            </div>
        )
    }

    private renderBottomBar() {
        if (this.state.isObjectSelected || this.state.mode === AnnotationMode.Draw) {
            return (
                <div className="AppCore__photo__AnnotationPage__bottomBar">
                    {this.renderBottomBarActionButton()}
                    <ColorSelector
                        disabled={this.state.isDrawing}
                        selectedColor={this.state.color}
                        onChange={this.handleColorChange}
                        isOpen={this.state.isColorSelectOpen}
                        setIsOpen={(isOpen) => this.setState({ isColorSelectOpen: isOpen })}
                    />
                </div>
            )
        }

        const cancelLabel = i18next.t('appcore:common.cancel')

        const saveLabel = i18next.t('appcore:common.save')

        const isSaving = this.promiseManagers.save.isInProgress()

        return (
            <div className="AppCore__photo__AnnotationPage__bottomBar">
                <div className="AppCore__photo__AnnotationPage__cancel" onClick={this.props.onCancel}>
                    {cancelLabel}
                </div>
                <div className="AppCore__photo__AnnotationPage__save" onClick={!isSaving ? this.onSave : () => null}>
                    {isSaving ? <Pulse /> : saveLabel}
                </div>
            </div>
        )
    }
}

interface ColorSelectorProps {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    selectedColor: string
    onChange: (newColor: string) => void
    disabled: boolean
}

const ColorSelector = ({ onChange, selectedColor, isOpen, setIsOpen, disabled }: ColorSelectorProps) => {
    const handleColorClick = (color: string) => {
        onChange(color)
        setIsOpen(false)
    }

    const getBorderColor = (color: string) => (color.toUpperCase() === '#FFFFFF' ? '#7E8B9E' : '#FFFFFF')

    if (isOpen && !disabled) {
        return (
            <div className="AppCore__photo__AnnotationPage__colorButtons">
                {COLORS.map((color) => (
                    <div
                        key={color}
                        className="AppCore__photo__AnnotationPage__colorButton"
                        style={{ backgroundColor: color, borderColor: getBorderColor(color) }}
                        onClick={() => handleColorClick(color)}
                    />
                ))}
                <div
                    className="AppCore__photo__AnnotationPage__colorSelectorCloseButton"
                    onClick={() => setIsOpen(false)}
                >
                    <zaptic-icon
                        class="AppCore__photo__AnnotationPage__colorSelectorClose"
                        icon-name="delete-grey-cross"
                    />
                </div>
            </div>
        )
    }

    const editIconColor = getBorderColor(selectedColor)

    return (
        <div
            className="AppCore__photo__AnnotationPage__selectedColorWrapper"
            onClick={() => {
                if (!disabled) setIsOpen(true)
            }}
        >
            <div
                style={{ backgroundColor: selectedColor, borderColor: editIconColor, color: editIconColor }}
                className={classNames('AppCore__photo__AnnotationPage__selectedColor', {
                    'AppCore__photo__AnnotationPage__selectedColor--disabled': disabled,
                })}
            >
                <zaptic-icon class="AppCore__photo__AnnotationPage__colorEditIcon" icon-name="edit" />
            </div>
        </div>
    )
}

interface ShapeSelectorProps {
    onShapeSelect: (shape: Shape) => void
}

const ShapeSelector = ({ onShapeSelect }: ShapeSelectorProps) => {
    const shapeLabel = i18next.t('appcore:AnnotationPage.shape')

    const [isOpen, setIsOpen] = useState(false)

    const handleTextClick = () => {
        setIsOpen((value) => !value)
    }

    return (
        <div
            className="AppCore__photo__AnnotationPage__action AppCore__photo__AnnotationPage__action--shape"
            onClick={handleTextClick}
        >
            <div className="AppCore__photo__AnnotationPage__actionIcon">
                <zaptic-icon class="test" icon-name="star" />
            </div>
            <div className="AppCore__photo__AnnotationPage__actionLabel">{shapeLabel}</div>
            {isOpen && (
                <div className="AppCore__photo__AnnotationPage__shapeSelector">
                    <div
                        className="AppCore__photo__AnnotationPage__shapeButton"
                        onClick={() => onShapeSelect(Shape.Square)}
                    >
                        <zaptic-icon class="AppCore__photo__AnnotationPage__shapeButtonIcon" icon-name="square" />
                    </div>
                    <div
                        className="AppCore__photo__AnnotationPage__shapeButton"
                        onClick={() => onShapeSelect(Shape.Arrow)}
                    >
                        <zaptic-icon
                            class="AppCore__photo__AnnotationPage__shapeButtonIcon"
                            icon-name="annotation-arrow"
                        />
                    </div>
                    <div
                        className="AppCore__photo__AnnotationPage__shapeButton"
                        onClick={() => onShapeSelect(Shape.Line)}
                    >
                        <zaptic-icon
                            class="AppCore__photo__AnnotationPage__shapeButtonIcon"
                            icon-name="annotation-line"
                        />
                    </div>
                    <div
                        className="AppCore__photo__AnnotationPage__shapeButton"
                        onClick={() => onShapeSelect(Shape.Circle)}
                    >
                        <zaptic-icon class="AppCore__photo__AnnotationPage__shapeButtonIcon" icon-name="circle" />
                    </div>
                </div>
            )}
        </div>
    )
}
